import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
    buildLackMessageByMap,
    getCountArticleList,
    getRepresentArticleList,
} from '@/shared/utils/created';
import { getTextbookLcms, getDataImportLcms } from '@/shared/api/create';
import { showToast } from '@/shared/utils/toast';

export interface CreatePopupStateType {
    selectNum: number;
    selectName: string;
    setState: <K extends keyof CreatePopupStateType>(
        key: K,
        value: CreatePopupStateType[K]
    ) => void;
}

export const useCreatePopupStore = create<CreatePopupStateType>((set) => ({
    selectNum: 0,
    selectName: '',
    setState: (key, value) =>
        set((state) => ({
            ...state,
            [key]: value,
        })),
}));

export interface CreateStateType {
    currentStep: number;
    type: LayoutType;
    selected: number[][];
    openSelected: number[][];
    articleType: DATA.ArticleType | undefined;
    category: DATA.LesnRscCategory | undefined;
    editExamId: number | undefined;
    publishId: number | undefined;
    editSubjectId: number | undefined;
    editExamName: string;
    editNewExamName: string;
    editExamSetId: string;
    editTextBookId: number | undefined;
    curriculumFilters: DATA.CurriculumProps[];
    difficultyFilters: DATA.CodeInfo[];
    creatorFilters: DATA.CodeInfo[];
    subjectAbilityFilters: DATA.CodeInfo[];
    evaluationAreaFilters: DATA.CodeInfo[];
    questionTypeFilters: DATA.CodeInfo[];
    sorting: number;
    curriCheckList: DATA.CurriculumProps[];
    difyCheckList: DATA.CodeInfo[];
    creatorCheckList: DATA.CreatorInfo[];
    subjectAbilityCheckList: DATA.CodeInfo[];
    evaluationAreaCheckList: DATA.CodeInfo[];
    questionTypeCheckList: DATA.CodeInfo[];
    sTextbookList: API.TextbookListbyCurriBook.TextbkListbyCurriBook[];
    initTextbookList: API.TextbookListbyCurriBook.TextbkListbyCurriBook[];
    sCurribook: API.TextbookListbyCurriBook.TextbkListbyCurriBook | undefined;
    sCurribookId: number | undefined;
    sGradeIndex: number | undefined;
    sSubjectList: API.TextbookSubject.SubjectData[];
    sTopMap: API.Descendants.MetaData | undefined;
    requestSave: string;
    sourceTypeList: CheckSelectTableData[];
    questionTypeList: CheckSelectTableData[];
    setList: DATA.ArticleInfo[];
    countList: DATA.ArticleInfo[];
    sDescendantsData: API.Descendants.MetaData[];
    saveEditedId: number | undefined;
    refreshArticleCount: number;
    editBackType: string;
    curTextbookId: number | undefined;
    dataTabFilterSelected: number[][];
    dataTabSelectDataType: CheckSelectTableData[];
    dataTabSelectDifficulty: CheckSelectTableData[];
    dataTabDifficultyList: API.MetaSearch.MetaData[];
    selectTab: 'data' | 'setMode';
    filterSelected: number[][];
    sourceType: CheckSelectTableData[];
    selectedEval: CheckSelectTableData[];
    isExceptQuestion: boolean;
    isExceptChain: boolean;
    difficultyList: API.MetaSearch.MetaData[] | undefined;
    questionType: number[];
    selectDifficulty: CheckSelectTableData[];
    selectedQuestion: CheckSelectTableData[];
    selectedType: string;
    high: number;
    middle: number;
    low: number;
    difficultyCountList: any[];
    dataTypeList: API.MetaSearch.MetaData[] | undefined;
    evalAreaList: API.MetaSearch.MetaData[] | undefined;
    initData: boolean;
    mapCode: string;
    setSelected: (data: number[][]) => void;
    setOpenSelected: (data: number[][]) => void;
    setState: <K extends keyof CreateStateType>(key: K, value: CreateStateType[K]) => void;
    setCurriculumFilters: (data: API.TextbookMetaCrcuList.Res) => {
        treeData: DATA.CurriculumProps[];
        totalCnt: number;
    };
    setSTextbookList: (params: API.TextbookListbyCurriBook.TextbkListbyCurriBook[]) => void;
    setInitTextbookList: (params: API.TextbookListbyCurriBook.TextbkListbyCurriBook[]) => void;
    setCurribook: (params: API.TextbookListbyCurriBook.TextbkListbyCurriBook) => Promise<void>;
    setSSubjectList: (params: API.TextbookSubject.SubjectData[]) => Promise<void>;
    setSTopMap: (params: API.Descendants.MetaData) => Promise<void>;
    fetchMetaData: () => Promise<void>;
    fetchInitSetList: (
        brand_id: number,
        curriBook: number,
        sourceTypeList: number[],
        questionType: number[],
        difficultyCountList: any[],
        studyMap: number[],
        evalList: number[],
        totalCnt: number,
        isExceptQuestion: boolean,
        isExceptChain: boolean,
        wrter_id: string,
        high?: number,
        middle?: number,
        low?: number
    ) => Promise<void>;
    setDescendantsData: (data: API.Descendants.MetaData[]) => void;
    setSaveEditedId: (data: number | undefined) => void;
    setSelectedQuestion: (data: CheckSelectTableData[]) => void;
}

export const useCreateStore = create<CreateStateType>()(
    persist(
        (set) => ({
            currentStep: 1,
            type: 'data',
            // 필터
            selected: [[], [], []],
            openSelected: [[], [], []],
            articleType: undefined,
            category: undefined,
            editExamId: undefined,
            publishId: undefined,
            editSubjectId: undefined,
            editExamName: '',
            editNewExamName: '',
            editExamSetId: '',
            editTextBookId: undefined,
            curriculumFilters: [],
            difficultyFilters: [],
            creatorFilters: [],
            subjectAbilityFilters: [],
            evaluationAreaFilters: [],
            questionTypeFilters: [],
            sorting: 2,
            curriCheckList: [],
            difyCheckList: [],
            creatorCheckList: [],
            subjectAbilityCheckList: [],
            evaluationAreaCheckList: [],
            questionTypeCheckList: [],
            sTextbookList: [],
            initTextbookList: [],
            sCurribook: undefined,
            sCurribookId: undefined,
            sGradeIndex: undefined,
            sSubjectList: [],
            sTopMap: undefined,
            requestSave: '',
            sourceTypeList: [],
            questionTypeList: [],
            setList: [],
            countList: [],
            sDescendantsData: [],
            saveEditedId: undefined,
            refreshArticleCount: 0,
            editBackType: 'normal',
            curTextbookId: undefined,
            dataTabFilterSelected: [[], [], []],
            dataTabSelectDataType: [],
            dataTabSelectDifficulty: [],
            selectTab: 'data',
            filterSelected: [[], [], []],
            sourceType: [],
            selectedEval: [],
            isExceptQuestion: false,
            isExceptChain: false,
            difficultyList: undefined,
            questionType: [],
            selectDifficulty: [],
            selectedQuestion: [],
            selectedType: 'auto',
            high: 5,
            middle: 10,
            low: 5,
            difficultyCountList: [],
            dataTypeList: undefined,
            evalAreaList: undefined,
            initData: false,
            mapCode: '',
            dataTabDifficultyList: [],
            setSelected: (data: number[][]) => set({ selected: data }),
            setOpenSelected: (data: number[][]) => set({ openSelected: data }),

            setState: (key, value) =>
                set((state) => ({
                    ...state,
                    [key]: value,
                })),

            setCurriculumFilters: (
                data: API.TextbookMetaCrcuList.Res
            ): { treeData: DATA.CurriculumProps[]; totalCnt: number } => {
                if (data) {
                    const _list = [...data];
                    const totalCnt = _list.length;
                    // curriculum 데이터 트리 구조 세팅 (parent 기준)
                    const getProps = (
                        data: API.TextbookMetaCrcuList.TextbookMeta
                    ): DATA.CurriculumProps => {
                        // 자식 curriculum data
                        const _children = _list.filter((c) => c.parentId == data.id);
                        const children = _children.map((c) => getProps(c)); // getProps 재귀(자식 children 초기화)
                        return {
                            id: data.id,
                            parent: data.parentId,
                            text: data.val,
                            depth: data.depth,
                            children,
                        } as DATA.CurriculumProps;
                    };
                    const rootList = _list.filter((c) => c.depth == 1);
                    const treeData = rootList.map((c) => getProps(c));
                    set((state) => ({
                        ...state,
                        curriculumFilters: treeData,
                    }));
                    return { treeData, totalCnt };
                }
                return {
                    treeData: [],
                    totalCnt: 0,
                };
            },
            setSTextbookList(textbookList) {
                set({ sTextbookList: textbookList });
            },
            setInitTextbookList(textbookList) {
                set({ initTextbookList: textbookList });
            },
            setCurribook: async (params) => {
                set({ sCurribook: params });
            },
            setSSubjectList: async (params) => {
                set({ sSubjectList: params });
            },
            setSTopMap: async (params) => {
                set({ sTopMap: params });
            },
            fetchMetaData: async () => {
                const [sourceTypeRes, questionTypeRes] = await Promise.all([
                    getTextbookLcms.getMetaList({ name: 'sourceType', brand_id: 6 }),
                    getTextbookLcms.getMetaList({ name: 'questionType' }),
                ]);

                set({
                    sourceTypeList: sourceTypeRes?.vo
                        .filter((el) => el.is_active === true && el.val !== '24' && el.id !== 62048)
                        .map(({ id, val }) => ({
                            id,
                            val: id === 60175 ? '학생용' : id === 60176 ? '교사용' : val,
                        })),
                    questionTypeList: questionTypeRes?.vo
                        .filter((el) => el.is_active === true)
                        .map(({ id, val }) => ({ id, val })),
                });
            },
            setSelectedQuestion: async (params) => {
                set({ selectedQuestion: params });
            },
            fetchInitSetList: async (
                brand_id,
                curriBook,
                sourceTypeList,
                questionType,
                difficultyCountList,
                studyMap,
                evalList,
                totalCnt,
                isExceptQuestion,
                isExceptChain,
                wrter_id
            ) => {
                const res = await getDataImportLcms.postArticleSearchEx({
                    brand_id: brand_id,
                    curriBook: curriBook,
                    limit_page: 1,
                    limit_pageItemCnt: totalCnt,
                    orderby_col: 'id',
                    orderby_dir: 'desc',
                    sourceTypeList: sourceTypeList,
                    questionTypeList: questionType,
                    isExceptQuestion: isExceptQuestion,
                    isExceptChain: isExceptChain,
                    difficultyCountList: difficultyCountList,
                    evaluationAreaList: evalList,
                    studyMapList: studyMap,
                    isSubmit: true,
                    wrter_id: wrter_id,
                });
                const data = res?.vo as DATA.ArticleInfo[];

                if (data && data.length > 0) {
                    const metaRes = await getDataImportLcms.postArticleMetaInfo({
                        articleIds: data.map((el) => el.id),
                    });
                    const metaMap = metaRes
                        ? Object.fromEntries(metaRes.vo.map((meta) => [meta.article_id, meta]))
                        : {};
                    const saveData = data.map((el) => {
                        const meta = metaMap[el.id];

                        return {
                            ...el,
                            articles: meta?.articles ?? [],
                            module_tc_use_cnt: meta?.module_tc_use_cnt ?? '0',
                            article_id: meta?.article_id,
                        };
                    });
                    set((state) => ({
                        setList: getRepresentArticleList(saveData),
                        countList: getCountArticleList(data),
                        refreshArticleCount: state.refreshArticleCount + 1,
                    }));
                } else {
                    set({
                        setList: [],
                    });
                    const text = buildLackMessageByMap(difficultyCountList);
                    showToast(text, 2000, 'error');
                }
            },
            setDescendantsData(data) {
                set({ sDescendantsData: data });
            },
            setSaveEditedId: async (params: number | undefined) => set({ saveEditedId: params }),
        }),
        {
            name: 'create-storage', // localStorage key
            partialize: (state) => ({
                currentStep: state.currentStep,
                editExamId: state.editExamId,
                editExamName: state.editExamName,
                editTextBookId: state.editTextBookId,
                editSubjectId: state.editSubjectId,
                sCurribookId: state.sCurribookId,
                sGradeIndex: state.sGradeIndex,
                sCurribook: state.sCurribook,
                sTextbookList: state.sTextbookList,
                type: state.type,
                publishId: state.publishId,
                setList: state.setList,
                curTextbookId: state.curTextbookId,
            }),
        }
    )
);

interface CreateSearchFilterStateType { }

export const useCreateSearchFilterState = create<CreateSearchFilterStateType>((set) => ({}));
